import React from 'react';

import {
  TextTag,
  Breakpoints,
  CTA,
  TextAppearance,
  TextSize,
  TextWeight,
  TextLineHeight,
  CustomImage,
} from '@vw-marketing/us-components';

import { AppConstants } from '../../utils/app-constants';
import {
  ChangeVehicleCTAContainer,
  ModelCardContainer,
  ModelCardContent,
  ModelCardImageContainer,
  ModelCardInfoSection,
} from './styles';
import { createFeatureHubHref } from '../../utils/navigation-url';
import { TextBox } from '../Box';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useHistory } from '../../hooks/use-history';

interface ModelCardProps {
  onChangeModel: () => void;
  model: string;
  modelYear: string;
  modelImage?: string;
}

export const ModelCard = ({
  model,
  modelYear,
  modelImage,
  onChangeModel,
}: ModelCardProps) => {
  const { faHostServerUrl = '' } = useFeatureAppConfig();
  const featureServices = useFeatureServices();
  const history = useHistory();

  const featureAppRoute = `/`;
  const serverRequest = featureServices['s2:server-request'];
  const serverRequestUrl = serverRequest?.url;
  const href = createFeatureHubHref(history, featureAppRoute, serverRequestUrl);

  return (
    <ModelCardContainer carImage={modelImage}>
      <ModelCardContent>
        <ModelCardInfoSection>
          <TextBox
            appearance={TextAppearance.copy200}
            tag={TextTag.p}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize14,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
            customStyle={(theme) => ({
              color: theme.colors.content.tertiary,
            })}
          >
            Quick Start Guide
          </TextBox>
          <TextBox
            appearance={TextAppearance.headline300}
            tag={TextTag.h1}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize24,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight28,
              },
              [Breakpoints.b1280]: {
                fontSize: TextSize.TextSize28,
                fontWeight: TextWeight.bold,
                lineHeight: TextLineHeight.TextLineHeight30,
              },
            }}
            wrapperStyle={{
              margin: 0,
            }}
          >
            {modelYear} {model}
          </TextBox>

          <ChangeVehicleCTAContainer>
            <CTA
              tag='a'
              href={href}
              emphasis='tertiary'
              aria-label='Change vehicle CTA'
              onClick={(e) => {
                e.preventDefault();
                onChangeModel();
              }}
            >
              <TextBox
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1280]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight28,
                  },
                }}
                wrapperStyle={(theme) => ({
                  margin: `${theme.size.static150} 0`,
                })}
              >
                Change Model
              </TextBox>
            </CTA>
          </ChangeVehicleCTAContainer>
        </ModelCardInfoSection>
        {modelImage && (
          <ModelCardImageContainer data-testid='car-image-section'>
            <CustomImage
              alt={`${model} Image`}
              src={modelImage}
              srcErrorImage={faHostServerUrl + AppConstants.Ngw6Logo480x216}
              srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
            />
          </ModelCardImageContainer>
        )}
      </ModelCardContent>
    </ModelCardContainer>
  );
};
